import React from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerItem } from '../utils/animations';
import { useTheme, getThemeColors } from '../contexts/ThemeContext';
import Section from './Section';
import Card from './Card';
import VideoPlayer from './VideoPlayer';

const About = () => {
  const { isDarkMode } = useTheme();
  const colors = getThemeColors(isDarkMode);
  
  const stats = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      number: "5,000",
      unit: "TPM",
      label: "Rolling Capacity"
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      number: "±0.5",
      unit: "mm",
      label: "Tolerance Precision"
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
        </svg>
      ),
      number: "15+",
      unit: "Grades",
      label: "Steel Varieties"
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      number: "48-72",
      unit: "Hours",
      label: "Delivery Window"
    }
  ];

  const features = [
    {
      title: "Modern Facility",
      description: "State-of-the-art re-rolling mill with automated process controls and continuous monitoring systems."
    },
    {
      title: "Quality Assurance",
      description: "In-house laboratory with advanced testing equipment ensuring every product meets stringent specifications."
    },
    {
      title: "Reliable Supply Chain",
      description: "Strategic partnerships and efficient logistics network enabling consistent, on-time deliveries nationwide."
    },
    {
      title: "Technical Expertise",
      description: "Experienced metallurgists and engineers providing custom solutions for specialized applications."
    }
  ];

  return (
    <Section id="about" background="texture" className="relative !py-3 lg:!py-24">
      <div className="flex flex-col lg:grid lg:grid-cols-2 gap-6 lg:gap-16 lg:items-center">
        {/* Right Column - Stats and Visual (First on mobile) */}
        <motion.div
          variants={staggerItem}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="flex flex-col space-y-4 lg:space-y-8 order-1 lg:order-2"
        >
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 order-2 lg:order-1">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                variants={fadeInUp}
                transition={{ delay: index * 0.15 }}
              >
                <Card 
                  hover={true} 
                  gradient={true}
                  className="p-4 sm:p-6 text-center"
                >
                  <div className="text-primary-400 mb-2 sm:mb-3 flex justify-center">
                    <div className="w-6 h-6 sm:w-8 sm:h-8">
                      {React.cloneElement(stat.icon, { className: "w-6 h-6 sm:w-8 sm:h-8" })}
                    </div>
                  </div>
                  <div className={`text-xl sm:text-2xl font-bold ${colors.text.primary} mb-1`}>
                    {stat.number}
                    <span className="text-primary-400 text-base sm:text-lg ml-1">{stat.unit}</span>
                  </div>
                  <div className={`${colors.text.muted} text-xs sm:text-sm leading-tight`}>
                    {stat.label}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Video Player - Steel Production Process */}
          <motion.div
            variants={fadeInUp}
            transition={{ delay: 0.6 }}
            className="order-1 lg:order-2"
          >
            <VideoPlayer
              youtubeUrl="https://www.youtube.com/watch?v=WHkKzjRmvHA"
              title="Modern Steel Processing"
              description="Our advanced re-rolling mill processes raw billets through controlled heating, precise rolling, and systematic cooling to achieve optimal mechanical properties."
            />
          </motion.div>

          {/* Value Proposition */}
          <motion.div
            variants={fadeInUp}
            transition={{ delay: 0.8 }}
            className="order-3 lg:order-3"
          >
            <Card glass={true} className="p-6 border-primary-400/30">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary-400 rounded-full flex items-center justify-center text-black">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-semibold mb-1">Quality Guarantee</h4>
                  <p className="text-neutral-400 text-sm">
                    Every product undergoes rigorous testing with full certification and traceability.
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Left Column - Content with Glassmorphism (Second on mobile) */}
        <motion.div
          variants={staggerItem}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="space-y-8 order-2 lg:order-1"
        >
          {/* Main content in glass panel */}
          <Card glass={true} className="p-8">
            <motion.div variants={fadeInUp}>
              <h2 className={`text-h2 font-bold ${colors.text.primary} mb-6`}>
                Excellence in Steel 
                <span className="text-primary-400"> Manufacturing</span>
              </h2>
              
              <p className={`text-body-lg ${colors.text.secondary} mb-6 leading-relaxed`}>
                Julfikar Steel operates a modern re-rolling facility with strict process controls 
                and a QA lab to ensure every length meets specification. Our commitment to precision 
                and reliability has made us a trusted partner in the steel industry.
              </p>
              
              <p className={`text-body ${colors.text.muted} leading-relaxed`}>
                From infrastructure projects to specialized industrial applications, 
                we deliver consistent mechanical properties with competitive lead times. 
                Our experienced team works closely with clients to provide tailored solutions 
                that meet exact requirements.
              </p>
            </motion.div>
          </Card>

          {/* Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
              >
                <Card hover={true} className={`p-6 h-full ${colors.bg.muted} backdrop-blur-sm`}>
                  <h4 className={`${colors.text.primary} font-semibold mb-2`}>{feature.title}</h4>
                  <p className={`${colors.text.muted} text-sm leading-relaxed`}>{feature.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

export default About;